import * as React from 'react';
import { useMemo } from 'react';
import { ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { formatDateTimeMediumDotted } from '@klareospace/pharos-lib-front-utils';
import { getFullName } from '@klareospace/pharos-lib-utils';
import {
  cn,
  createActionsColumn,
  DataTable,
  IconText,
  TextCell,
  type DataTableColumn,
} from '@klareospace/pharos-lib-web-utils';

import { PROOF_STEP_LABEL_KEYS, PROOF_TYPE_ICONS, PROOF_TYPE_LABEL_KEYS, type JobProof } from '../utils/proof-labels';

// Note: PROOF_STEP_LABEL_KEYS resolves to cross-namespace `orders:drawer.*` keys — this
// component reads the `t` function from the `tasks` namespace below, and i18next resolves
// `namespace:key` strings regardless of the active namespace.

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ProofsTableProps {
  /** Optional content rendered above the table, inside the same rounded card (e.g. a "Task X of Y" label). */
  readonly header?: React.ReactNode;
  /** Called with the clicked job/proof pair when its "View" action is triggered. */
  readonly onView: (pair: JobProof) => void;
  /** Job/proof pairs — already flattened + sorted by `flattenJobProofs`, rendered in the given order. */
  readonly proofs: JobProof[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

const CELL_CLASS_NAME = 'px-3 py-2';

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Headerless compact table of proofs for one task — reused as-is by
 * `OrderProofsTab` (which passes a "Task X of Y" `header`) and
 * `TaskProofsSection` (which passes no `header`). Renders its own empty state
 * so both call sites get consistent "no proof" messaging for free.
 *
 * Owns the rounded/bordered card look itself (single `overflow-hidden
 * rounded-lg border` wrapper around the optional header and the table) so
 * both call sites render an identical card instead of each re-declaring the
 * same border classes around this component — a `<table>` element's own
 * border-radius/overflow clipping is not reliable across browsers, so the
 * rounding is applied to a plain wrapping `<div>` instead.
 *
 * Built on the shared `DataTable` (with `hideHeader`, since this compact
 * per-task table has no column headers per the mockup) and its cell helpers
 * (`IconText`, `TextCell`, `createActionsColumn`) rather than hand-rolled div
 * rows. Column widths are not hardcoded — a real `<table>` naturally aligns
 * column widths across rows without needing per-cell width classes.
 */
export function ProofsTable({ header, onView, proofs }: ProofsTableProps): React.JSX.Element {
  const { t, i18n } = useTranslation('tasks');

  const columns = useMemo<DataTableColumn<JobProof>[]>(
    () => [
      {
        cell: (pair): React.ReactNode => (
          <span className="text-xs font-medium text-foreground">{t(PROOF_STEP_LABEL_KEYS[pair.jobType])}</span>
        ),
        cellClassName: CELL_CLASS_NAME,
        header: '',
        key: 'step',
      },
      {
        cell: (pair): React.ReactNode => (
          <IconText icon={PROOF_TYPE_ICONS[pair.proof.type]} size="xs">
            {t(PROOF_TYPE_LABEL_KEYS[pair.proof.type])}
          </IconText>
        ),
        cellClassName: CELL_CLASS_NAME,
        header: '',
        key: 'type',
      },
      {
        cell: (pair): React.ReactNode => (
          <TextCell className="text-xs">
            {getFullName(pair.proof.createdBy.firstName, pair.proof.createdBy.lastName)}
          </TextCell>
        ),
        cellClassName: CELL_CLASS_NAME,
        header: '',
        key: 'user',
      },
      {
        cell: (pair): React.ReactNode => (
          <TextCell className="text-xs" truncate={false}>
            {formatDateTimeMediumDotted(pair.proof.createdAt, i18n.language)}
          </TextCell>
        ),
        cellClassName: cn(CELL_CLASS_NAME, 'whitespace-nowrap'),
        header: '',
        key: 'date',
      },
      {
        ...createActionsColumn<JobProof>((pair): React.ReactNode => (
          <button
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
            onClick={(): void => onView(pair)}
            type="button"
          >
            <ExternalLink className="h-3 w-3" />
            {t('proofs.view')}
          </button>
        )),
        cellClassName: CELL_CLASS_NAME,
      },
    ],
    [i18n.language, onView, t],
  );

  if (proofs.length === 0 && !header) {
    return <span className="text-sm text-muted-foreground">{t('proofs.empty')}</span>;
  }

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      {header}
      {proofs.length === 0 ? (
        <span className="block px-3 py-2 text-sm text-muted-foreground">{t('proofs.empty')}</span>
      ) : (
        <DataTable
          ariaLabel={t('proofs.ariaLabel')}
          columns={columns}
          hideHeader
          rowKey={(pair): string => pair.proof.id}
          rows={proofs}
          tableClassName="[&_tr:last-child]:border-b-0"
        />
      )}
    </div>
  );
}
